import type { SummonKind } from "../../../data/portal-ffa-catalog";
import { applyTroopSummon } from "../economy-rules";
import { FfaEvent } from "../sim-events";
import type {
  SimEntity,
  SummonFailReason,
  SummonRequestResult,
  TeamState,
  WorldState,
} from "./types";
import {
  armyCount,
  C,
  emit,
  recalcSpecialResidual,
  spawnTroop,
  unitDef,
} from "./shared";

export function canAffordSummon(
  team: TeamState,
  kind: SummonKind,
): SummonFailReason | null {
  if (kind === "hero") {
    if (team.sigils < C.units.hero.sigilCost) return "sigil";
  } else if (kind === "weapon") {
    if (team.sigils < C.units.weapon.sigilCost) return "sigil";
  } else if (team.ledger.walletMana < unitDef(kind).summonCost) {
    return "mana";
  }
  return null;
}

export function summonFailReason(
  world: WorldState,
  teamId: number,
  kind: SummonKind,
): SummonFailReason | null {
  const team = world.teams[teamId];
  if (!team?.alive) return "dead";
  const afford = canAffordSummon(team, kind);
  if (afford) return afford;
  if (team.summonCd > 0 && team.summonQueue.length >= C.summon.queueDepth) {
    return "queue_full";
  }
  return null;
}

/** Immediate spawn when off CD; otherwise rejects with reason (no queue). */
export function trySummon(
  world: WorldState,
  teamId: number,
  kind: SummonKind,
): boolean {
  return requestSummon(world, teamId, kind, { allowQueue: false }).status ===
    "spawned";
}

/**
 * Player/UI entry: spawn now, or enqueue while on summon CD.
 * Permanent fails (mana/sigil/dead) reject without queueing.
 */
export function requestSummon(
  world: WorldState,
  teamId: number,
  kind: SummonKind,
  opts?: { allowQueue?: boolean },
): SummonRequestResult {
  const allowQueue = opts?.allowQueue !== false;
  const team = world.teams[teamId];
  if (!team?.alive) return { status: "rejected", reason: "dead" };
  const master = world.entities.find((e) => e.id === team.masterId);
  if (!master) return { status: "rejected", reason: "no_master" };

  const afford = canAffordSummon(team, kind);
  if (afford) return { status: "rejected", reason: afford };

  if (team.summonCd > 0) {
    if (!allowQueue) return { status: "rejected", reason: "cd" };
    if (team.summonQueue.length >= C.summon.queueDepth) {
      return { status: "rejected", reason: "queue_full" };
    }
    team.summonQueue.push(kind);
    return { status: "queued" };
  }

  return spawnSummonNow(world, team, master, kind)
    ? { status: "spawned" }
    : { status: "rejected", reason: "dead" };
}

export function spawnSummonNow(
  world: WorldState,
  team: TeamState,
  master: SimEntity,
  kind: SummonKind,
): boolean {
  const teamId = team.id;
  const def = unitDef(kind);

  let spawned: SimEntity | null = null;
  if (kind === "hero" || kind === "weapon") {
    const need =
      kind === "hero" ? C.units.hero.sigilCost : C.units.weapon.sigilCost;
    if (team.sigils < need) return false;
    team.sigils -= need;
    spawned = spawnTroop(world, teamId, kind, master.x, master.y);
    if (!spawned) {
      team.sigils += need;
      return false;
    }
    recalcSpecialResidual(world, teamId);
  } else {
    const next = applyTroopSummon(team.ledger, def.summonCost);
    if (!next) return false;
    team.ledger = next;
    spawned = spawnTroop(world, teamId, kind, master.x, master.y);
    if (!spawned) {
      team.ledger = {
        ...team.ledger,
        walletMana: team.ledger.walletMana + def.summonCost,
        aliveUnitValue: Math.max(
          0,
          team.ledger.aliveUnitValue - def.summonCost,
        ),
      };
      return false;
    }
  }

  emit(world, {
    kind: FfaEvent.Summon,
    time: world.time,
    entityId: spawned.id,
    teamId,
    x: spawned.x,
    y: spawned.y,
    role: kind,
  });

  team.summonCd = C.economy.summonIntervalSec;
  if (team.protectUntil > world.time) team.protectUntil = world.time;
  const army = armyCount(world, teamId);
  team.peakArmy = Math.max(team.peakArmy, army);
  return true;
}

export function drainSummonQueues(world: WorldState): void {
  for (const team of world.teams) {
    if (!team.alive || team.summonCd > 0) continue;
    while (team.summonQueue.length > 0 && team.summonCd <= 0) {
      const kind = team.summonQueue.shift()!;
      const master = world.entities.find((e) => e.id === team.masterId);
      if (!master) {
        team.summonQueue.length = 0;
        break;
      }
      if (canAffordSummon(team, kind)) continue;
      if (!spawnSummonNow(world, team, master, kind)) continue;
    }
  }
}
